/**
 * Textes de la page de rendez-vous, par langue. Le français fait foi ; chaque autre langue doit
 * fournir toutes les clés (le type l'impose). Ajouter une langue = ajouter un dictionnaire ici et
 * son libellé dans LOCALES. Dates et heures passent par `Intl` dans la langue choisie.
 */

export const LOCALES = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'ar', label: 'العربية' },
] as const;

export type Locale = (typeof LOCALES)[number]['code'];

export function isLocale(value: string): value is Locale {
  return LOCALES.some((locale) => locale.code === value);
}

/** Langue proposée par défaut : la première langue du navigateur que la page connaît. */
export function defaultLocale(languages: readonly string[]): Locale {
  for (const tag of languages) {
    const base = tag.split('-')[0]!.toLowerCase();
    if (isLocale(base)) return base;
  }
  return 'fr';
}

/** Étiquette BCP 47 pour `Intl`. Arabe : chiffres latins, comme les numéros saisis dans le formulaire. */
export function intlLocale(locale: Locale): string {
  return { fr: 'fr-FR', en: 'en-GB', de: 'de-DE', es: 'es-ES', it: 'it-IT', pt: 'pt-PT', nl: 'nl-NL', ar: 'ar-u-nu-latn' }[locale];
}

const fr = {
  consultant_option: 'Je souhaite qu’un consultant AYUMIA assiste à l’entretien',
  consultant_help: 'L’entretien est mené par l’IA ; un consultant peut y assister en plus et intervenir si besoin.',
  start_interview: 'Démarrer l’entretien (démo)',
  language: 'Langue',
  demo_footnote: 'Démonstration : les créneaux et la réservation sont simulés, aucune donnée n’est envoyée.',
  landing_title: 'Préparons votre AYUMIA',
  landing_lead:
    'Réservez un entretien d’une heure. Vous nous dites qui vous êtes et ce que vous voulez couvrir ; AYUMIA prépare l’entretien à l’avance, pour vous poser seulement les questions utiles.',
  landing_step1_title: 'Choisissez un créneau',
  landing_step1_text: 'Le jour et l’heure qui vous conviennent.',
  landing_step2_title: 'Répondez à quelques questions',
  landing_step2_text: 'Votre entreprise, vos outils, ce que vous voulez gérer. Deux minutes.',
  landing_step3_title: 'L’entretien',
  landing_step3_text: 'AYUMIA vous présente ce qu’elle a compris, vous corrigez, et elle prépare une proposition de configuration à valider.',
  landing_cta: 'Choisir un créneau',
  landing_note: 'Rien n’est configuré sans votre validation.',
  event_label: 'Rendez-vous',
  event_title: 'Entretien de préparation AYUMIA',
  event_channel: 'Visioconférence (lien envoyé avec la confirmation)',
  event_description:
    'Un échange guidé pour comprendre votre activité, vos outils actuels et ce que vous voulez améliorer. AYUMIA prépare l’entretien à partir des informations que vous donnez ici ; ses propositions restent à valider par vous.',
  minutes: (n: number) => `${n} min`,
  back_home: 'Retour à l’accueil',
  schedule_title: 'Choisissez une date et une heure',
  previous_month: 'Mois précédent',
  next_month: 'Mois suivant',
  days_of: (month: string) => `Jours de ${month}`,
  day_slots: (day: string, n: number) => `${day}, ${n} créneau${n > 1 ? 'x' : ''}`,
  day_unavailable: (day: string) => `${day}, indisponible`,
  slots_of: (day: string) => `Créneaux du ${day}`,
  availability_error: 'Les disponibilités n’ont pas pu être chargées.',
  retry: 'Réessayer',
  no_slot_month: 'Aucun créneau libre ce mois-ci. Essayez le mois suivant.',
  no_slot_day: 'Plus de créneau libre ce jour-là.',
  time_zone: 'Fuseau horaire',
  next: 'Suivant',
  change_slot: 'Changer de créneau',
  details_title: 'Vos informations',
  section_company: 'Votre entreprise',
  section_you: 'Vous',
  section_project: 'Votre projet',
  country: 'Pays',
  legal_name: 'Raison sociale',
  website: 'Site web',
  optional: 'Facultatif.',
  contact_name: 'Nom et prénom',
  contact_role: 'Fonction',
  email: 'E-mail',
  email_help: 'Pour la confirmation et le lien de l’entretien.',
  other_system: 'Quel autre outil ?',
  objective: 'Qu’aimeriez-vous améliorer en priorité ?',
  objective_placeholder: 'Par exemple : suivre les lots de la réception à l’expédition, arrêter les doubles saisies entre Excel et la facturation…',
  characters_left: (n: number) => `${n} caractère${n > 1 ? 's' : ''} restant${n > 1 ? 's' : ''}.`,
  required_mark: 'obligatoire',
  valid: 'Valide',
  error_legal_name: 'Indiquez le nom de votre entreprise.',
  error_registration_format: 'Ce numéro n’a pas le format attendu.',
  error_registration_checksum: 'Ce numéro ne passe pas le contrôle de clé. Vérifiez-le.',
  error_siren_format: 'Un SIREN compte 9 chiffres, un SIRET 14.',
  error_website: 'Cette adresse de site semble incomplète.',
  error_contact_name: 'Indiquez votre nom.',
  error_email_missing: 'Indiquez une adresse e-mail.',
  error_email_format: 'Cette adresse e-mail semble incomplète.',
  error_domains: 'Choisissez au moins un domaine.',
  error_form: (n: number) => `${n} champ${n > 1 ? 's' : ''} à corriger.`,
  submit: 'Planifier l’entretien',
  submitting: 'Réservation…',
  privacy_note: 'Vos réponses servent uniquement à préparer l’entretien. Elles restent des déclarations : AYUMIA vous les fera confirmer.',
  slot_taken: 'Ce créneau vient d’être pris. Choisissez-en un autre.',
  booking_failed: 'La réservation n’a pas abouti. Réessayez dans un instant.',
  confirmed_title: 'Votre entretien est planifié',
  confirmation_sent: (email: string) => `Une confirmation sera envoyée à ${email}.`,
  add_to_calendar: 'Ajouter à mon agenda (.ics)',
  ics_summary: (company: string) => `Entretien de préparation AYUMIA — ${company}`,
  ics_description: 'Entretien de préparation de votre environnement AYUMIA. Le lien de visioconférence vous sera envoyé par e-mail.',
  ics_file: 'entretien-ayumia.ics',
  understood_title: 'Ce que vous nous avez dit',
  understood_lead: 'AYUMIA partira de ces déclarations et vous les fera confirmer au début de l’entretien.',
  company: 'Entreprise',
  declared: 'Déclaré',
  probable_domains: (list: string) => `Domaines probables de l’entretien : ${list}.`,
  objective_fact: 'Objectif principal',
  prepare_title: 'Aidez AYUMIA à préparer l’entretien',
  prepare_lead: 'Facultatif. Plus AYUMIA en sait avant l’entretien, moins elle aura de questions à vous poser.',
  prepare_skipped: 'C’est noté : nous préparerons l’entretien avec ce que vous avez déjà indiqué.',
  prepare_file: 'Ajouter un fichier',
  prepare_system: 'Connecter un système',
  prepare_note: 'Ajouter une information',
  prepare_skip: 'Passer cette étape',
  prepare_file_added: 'Fichier (simulé)',
  prepare_system_added: 'Connexion à un système (simulée)',
  note_label: 'Information pour AYUMIA',
  add: 'Ajouter',
  kind_file: 'Fichier',
  kind_system: 'Système',
  kind_note: 'Information',
  prepare_demo: 'Démonstration : l’ajout de fichiers et la connexion de systèmes sont simulés. Aucun identifiant ne vous sera jamais demandé ici.',
  demo_reference: (id: string) => `Référence de démonstration : ${id}`,
  list_separator: ', ',
};

export type Messages = typeof fr;

const en: Messages = {
  consultant_option: 'I would like an AYUMIA consultant to attend the interview',
  consultant_help: 'The interview is led by the AI; a consultant can also attend and step in if needed.',
  start_interview: 'Start the interview (demo)',
  language: 'Language',
  demo_footnote: 'Demo: slots and booking are simulated; no data is sent.',
  landing_title: 'Let’s prepare your AYUMIA',
  landing_lead:
    'Book a one-hour interview. Tell us who you are and what you want to cover; AYUMIA prepares the interview in advance so it only asks the questions that matter.',
  landing_step1_title: 'Pick a time',
  landing_step1_text: 'The day and time that suit you.',
  landing_step2_title: 'Answer a few questions',
  landing_step2_text: 'Your company, your tools, what you want to manage. Two minutes.',
  landing_step3_title: 'The interview',
  landing_step3_text: 'AYUMIA shows you what it understood, you correct it, and it prepares a configuration proposal for you to approve.',
  landing_cta: 'Pick a time',
  landing_note: 'Nothing is configured without your approval.',
  event_label: 'Appointment',
  event_title: 'AYUMIA preparation interview',
  event_channel: 'Video call (link sent with the confirmation)',
  event_description:
    'A guided conversation to understand your business, your current tools and what you want to improve. AYUMIA prepares the interview from what you share here; its proposals remain yours to approve.',
  minutes: (n: number) => `${n} min`,
  back_home: 'Back to start',
  schedule_title: 'Select a date and time',
  previous_month: 'Previous month',
  next_month: 'Next month',
  days_of: (month: string) => `Days of ${month}`,
  day_slots: (day: string, n: number) => `${day}, ${n} time slot${n > 1 ? 's' : ''}`,
  day_unavailable: (day: string) => `${day}, unavailable`,
  slots_of: (day: string) => `Time slots for ${day}`,
  availability_error: 'Availability could not be loaded.',
  retry: 'Try again',
  no_slot_month: 'No free slot this month. Try the next one.',
  no_slot_day: 'No free slot left that day.',
  time_zone: 'Time zone',
  next: 'Next',
  change_slot: 'Change time slot',
  details_title: 'Your details',
  section_company: 'Your company',
  section_you: 'You',
  section_project: 'Your project',
  country: 'Country',
  legal_name: 'Company name',
  website: 'Website',
  optional: 'Optional.',
  contact_name: 'Full name',
  contact_role: 'Job title',
  email: 'Email',
  email_help: 'For the confirmation and the interview link.',
  other_system: 'Which other tool?',
  objective: 'What would you like to improve first?',
  objective_placeholder: 'For example: track lots from receipt to shipping, stop double entry between Excel and invoicing…',
  characters_left: (n: number) => `${n} character${n > 1 ? 's' : ''} left.`,
  required_mark: 'required',
  valid: 'Valid',
  error_legal_name: 'Enter your company name.',
  error_registration_format: 'This number does not have the expected format.',
  error_registration_checksum: 'This number fails the check-digit test. Please check it.',
  error_siren_format: 'A SIREN has 9 digits, a SIRET 14.',
  error_website: 'This website address looks incomplete.',
  error_contact_name: 'Enter your name.',
  error_email_missing: 'Enter an email address.',
  error_email_format: 'This email address looks incomplete.',
  error_domains: 'Choose at least one area.',
  error_form: (n: number) => `${n} field${n > 1 ? 's' : ''} to fix.`,
  submit: 'Schedule the interview',
  submitting: 'Booking…',
  privacy_note: 'Your answers are only used to prepare the interview. They remain statements: AYUMIA will ask you to confirm them.',
  slot_taken: 'This time slot was just taken. Please choose another one.',
  booking_failed: 'The booking did not go through. Please try again in a moment.',
  confirmed_title: 'Your interview is scheduled',
  confirmation_sent: (email: string) => `A confirmation will be sent to ${email}.`,
  add_to_calendar: 'Add to my calendar (.ics)',
  ics_summary: (company: string) => `AYUMIA preparation interview — ${company}`,
  ics_description: 'Preparation interview for your AYUMIA environment. The video call link will be sent by email.',
  ics_file: 'ayumia-interview.ics',
  understood_title: 'What you told us',
  understood_lead: 'AYUMIA will start from these statements and ask you to confirm them at the beginning of the interview.',
  company: 'Company',
  declared: 'Stated',
  probable_domains: (list: string) => `Likely interview topics: ${list}.`,
  objective_fact: 'Main objective',
  prepare_title: 'Help AYUMIA prepare the interview',
  prepare_lead: 'Optional. The more AYUMIA knows beforehand, the fewer questions it will need to ask.',
  prepare_skipped: 'Noted: we will prepare the interview with what you have already shared.',
  prepare_file: 'Add a file',
  prepare_system: 'Connect a system',
  prepare_note: 'Add information',
  prepare_skip: 'Skip this step',
  prepare_file_added: 'File (simulated)',
  prepare_system_added: 'System connection (simulated)',
  note_label: 'Information for AYUMIA',
  add: 'Add',
  kind_file: 'File',
  kind_system: 'System',
  kind_note: 'Information',
  prepare_demo: 'Demo: adding files and connecting systems are simulated. You will never be asked for credentials here.',
  demo_reference: (id: string) => `Demo reference: ${id}`,
  list_separator: ', ',
};

const de: Messages = {
  consultant_option: 'Ich möchte, dass ein AYUMIA-Berater am Gespräch teilnimmt',
  consultant_help: 'Das Gespräch führt die KI; ein Berater kann zusätzlich teilnehmen und bei Bedarf eingreifen.',
  start_interview: 'Gespräch starten (Demo)',
  language: 'Sprache',
  demo_footnote: 'Demo: Termine und Buchung sind simuliert, es werden keine Daten übermittelt.',
  landing_title: 'Bereiten wir Ihr AYUMIA vor',
  landing_lead:
    'Buchen Sie ein einstündiges Gespräch. Sie sagen uns, wer Sie sind und was Sie abdecken möchten; AYUMIA bereitet das Gespräch vor und stellt Ihnen nur die nötigen Fragen.',
  landing_step1_title: 'Termin wählen',
  landing_step1_text: 'Der Tag und die Uhrzeit, die Ihnen passen.',
  landing_step2_title: 'Ein paar Fragen beantworten',
  landing_step2_text: 'Ihr Unternehmen, Ihre Werkzeuge, was Sie verwalten möchten. Zwei Minuten.',
  landing_step3_title: 'Das Gespräch',
  landing_step3_text: 'AYUMIA zeigt Ihnen, was es verstanden hat, Sie korrigieren, und es erstellt einen Konfigurationsvorschlag zur Freigabe.',
  landing_cta: 'Termin wählen',
  landing_note: 'Ohne Ihre Freigabe wird nichts konfiguriert.',
  event_label: 'Termin',
  event_title: 'AYUMIA-Vorbereitungsgespräch',
  event_channel: 'Videokonferenz (Link mit der Bestätigung)',
  event_description:
    'Ein geführtes Gespräch, um Ihre Tätigkeit, Ihre heutigen Werkzeuge und Ihre Verbesserungswünsche zu verstehen. AYUMIA bereitet das Gespräch mit Ihren Angaben vor; seine Vorschläge geben Sie selbst frei.',
  minutes: (n: number) => `${n} Min.`,
  back_home: 'Zurück zum Start',
  schedule_title: 'Datum und Uhrzeit wählen',
  previous_month: 'Vorheriger Monat',
  next_month: 'Nächster Monat',
  days_of: (month: string) => `Tage im ${month}`,
  day_slots: (day: string, n: number) => `${day}, ${n} ${n > 1 ? 'freie Termine' : 'freier Termin'}`,
  day_unavailable: (day: string) => `${day}, nicht verfügbar`,
  slots_of: (day: string) => `Termine am ${day}`,
  availability_error: 'Die Verfügbarkeiten konnten nicht geladen werden.',
  retry: 'Erneut versuchen',
  no_slot_month: 'Kein freier Termin in diesem Monat. Versuchen Sie den nächsten.',
  no_slot_day: 'An diesem Tag ist kein Termin mehr frei.',
  time_zone: 'Zeitzone',
  next: 'Weiter',
  change_slot: 'Termin ändern',
  details_title: 'Ihre Angaben',
  section_company: 'Ihr Unternehmen',
  section_you: 'Sie',
  section_project: 'Ihr Projekt',
  country: 'Land',
  legal_name: 'Firmenname',
  website: 'Website',
  optional: 'Optional.',
  contact_name: 'Vor- und Nachname',
  contact_role: 'Funktion',
  email: 'E-Mail',
  email_help: 'Für die Bestätigung und den Link zum Gespräch.',
  other_system: 'Welches andere Werkzeug?',
  objective: 'Was möchten Sie vorrangig verbessern?',
  objective_placeholder: 'Zum Beispiel: Chargen vom Wareneingang bis zum Versand verfolgen, Doppelerfassung zwischen Excel und Rechnungsstellung beenden…',
  characters_left: (n: number) => `Noch ${n} Zeichen.`,
  required_mark: 'Pflichtfeld',
  valid: 'Gültig',
  error_legal_name: 'Geben Sie den Namen Ihres Unternehmens an.',
  error_registration_format: 'Diese Nummer hat nicht das erwartete Format.',
  error_registration_checksum: 'Diese Nummer besteht die Prüfziffernkontrolle nicht. Bitte prüfen.',
  error_siren_format: 'Eine SIREN hat 9 Ziffern, eine SIRET 14.',
  error_website: 'Diese Website-Adresse scheint unvollständig.',
  error_contact_name: 'Geben Sie Ihren Namen an.',
  error_email_missing: 'Geben Sie eine E-Mail-Adresse an.',
  error_email_format: 'Diese E-Mail-Adresse scheint unvollständig.',
  error_domains: 'Wählen Sie mindestens einen Bereich.',
  error_form: (n: number) => `${n} ${n > 1 ? 'Felder' : 'Feld'} zu korrigieren.`,
  submit: 'Gespräch planen',
  submitting: 'Buchung…',
  privacy_note: 'Ihre Antworten dienen nur der Vorbereitung des Gesprächs. Sie bleiben Angaben: AYUMIA lässt sie von Ihnen bestätigen.',
  slot_taken: 'Dieser Termin wurde gerade vergeben. Bitte wählen Sie einen anderen.',
  booking_failed: 'Die Buchung ist fehlgeschlagen. Bitte versuchen Sie es gleich noch einmal.',
  confirmed_title: 'Ihr Gespräch ist geplant',
  confirmation_sent: (email: string) => `Eine Bestätigung wird an ${email} gesendet.`,
  add_to_calendar: 'Zu meinem Kalender hinzufügen (.ics)',
  ics_summary: (company: string) => `AYUMIA-Vorbereitungsgespräch — ${company}`,
  ics_description: 'Vorbereitungsgespräch für Ihre AYUMIA-Umgebung. Der Link zur Videokonferenz wird per E-Mail gesendet.',
  ics_file: 'ayumia-gespraech.ics',
  understood_title: 'Was Sie uns gesagt haben',
  understood_lead: 'AYUMIA geht von diesen Angaben aus und lässt sie zu Beginn des Gesprächs bestätigen.',
  company: 'Unternehmen',
  declared: 'Angegeben',
  probable_domains: (list: string) => `Voraussichtliche Themen des Gesprächs: ${list}.`,
  objective_fact: 'Hauptziel',
  prepare_title: 'Helfen Sie AYUMIA bei der Vorbereitung',
  prepare_lead: 'Optional. Je mehr AYUMIA vorher weiß, desto weniger Fragen muss es stellen.',
  prepare_skipped: 'Notiert: Wir bereiten das Gespräch mit Ihren bisherigen Angaben vor.',
  prepare_file: 'Datei hinzufügen',
  prepare_system: 'System verbinden',
  prepare_note: 'Information hinzufügen',
  prepare_skip: 'Diesen Schritt überspringen',
  prepare_file_added: 'Datei (simuliert)',
  prepare_system_added: 'Systemverbindung (simuliert)',
  note_label: 'Information für AYUMIA',
  add: 'Hinzufügen',
  kind_file: 'Datei',
  kind_system: 'System',
  kind_note: 'Information',
  prepare_demo: 'Demo: Dateien und Systemverbindungen sind simuliert. Hier werden Sie nie nach Zugangsdaten gefragt.',
  demo_reference: (id: string) => `Demo-Referenz: ${id}`,
  list_separator: ', ',
};

const es: Messages = {
  consultant_option: 'Deseo que un consultor de AYUMIA asista a la entrevista',
  consultant_help: 'La entrevista la dirige la IA; un consultor puede asistir además e intervenir si es necesario.',
  start_interview: 'Iniciar la entrevista (demo)',
  language: 'Idioma',
  demo_footnote: 'Demostración: los horarios y la reserva son simulados; no se envía ningún dato.',
  landing_title: 'Preparemos su AYUMIA',
  landing_lead:
    'Reserve una entrevista de una hora. Díganos quién es y qué quiere cubrir; AYUMIA prepara la entrevista de antemano para hacerle solo las preguntas útiles.',
  landing_step1_title: 'Elija un horario',
  landing_step1_text: 'El día y la hora que le convengan.',
  landing_step2_title: 'Responda a algunas preguntas',
  landing_step2_text: 'Su empresa, sus herramientas, lo que quiere gestionar. Dos minutos.',
  landing_step3_title: 'La entrevista',
  landing_step3_text: 'AYUMIA le presenta lo que ha entendido, usted corrige y prepara una propuesta de configuración para que la valide.',
  landing_cta: 'Elegir un horario',
  landing_note: 'No se configura nada sin su validación.',
  event_label: 'Cita',
  event_title: 'Entrevista de preparación AYUMIA',
  event_channel: 'Videoconferencia (enlace enviado con la confirmación)',
  event_description:
    'Una conversación guiada para entender su actividad, sus herramientas actuales y lo que quiere mejorar. AYUMIA prepara la entrevista con la información que nos da aquí; sus propuestas quedan pendientes de su validación.',
  minutes: (n: number) => `${n} min`,
  back_home: 'Volver al inicio',
  schedule_title: 'Elija una fecha y una hora',
  previous_month: 'Mes anterior',
  next_month: 'Mes siguiente',
  days_of: (month: string) => `Días de ${month}`,
  day_slots: (day: string, n: number) => `${day}, ${n} horario${n > 1 ? 's' : ''}`,
  day_unavailable: (day: string) => `${day}, no disponible`,
  slots_of: (day: string) => `Horarios del ${day}`,
  availability_error: 'No se pudo cargar la disponibilidad.',
  retry: 'Reintentar',
  no_slot_month: 'No hay horarios libres este mes. Pruebe el siguiente.',
  no_slot_day: 'Ya no quedan horarios libres ese día.',
  time_zone: 'Zona horaria',
  next: 'Siguiente',
  change_slot: 'Cambiar de horario',
  details_title: 'Sus datos',
  section_company: 'Su empresa',
  section_you: 'Usted',
  section_project: 'Su proyecto',
  country: 'País',
  legal_name: 'Razón social',
  website: 'Sitio web',
  optional: 'Opcional.',
  contact_name: 'Nombre y apellidos',
  contact_role: 'Cargo',
  email: 'Correo electrónico',
  email_help: 'Para la confirmación y el enlace de la entrevista.',
  other_system: '¿Qué otra herramienta?',
  objective: '¿Qué le gustaría mejorar en primer lugar?',
  objective_placeholder: 'Por ejemplo: seguir los lotes desde la recepción hasta la expedición, acabar con la doble entrada entre Excel y la facturación…',
  characters_left: (n: number) => `Quedan ${n} caracteres.`,
  required_mark: 'obligatorio',
  valid: 'Válido',
  error_legal_name: 'Indique el nombre de su empresa.',
  error_registration_format: 'Este número no tiene el formato esperado.',
  error_registration_checksum: 'Este número no supera el control de dígito. Compruébelo.',
  error_siren_format: 'Un SIREN tiene 9 cifras, un SIRET 14.',
  error_website: 'Esta dirección web parece incompleta.',
  error_contact_name: 'Indique su nombre.',
  error_email_missing: 'Indique una dirección de correo.',
  error_email_format: 'Esta dirección de correo parece incompleta.',
  error_domains: 'Elija al menos un ámbito.',
  error_form: (n: number) => `${n} campo${n > 1 ? 's' : ''} por corregir.`,
  submit: 'Programar la entrevista',
  submitting: 'Reservando…',
  privacy_note: 'Sus respuestas solo sirven para preparar la entrevista. Siguen siendo declaraciones: AYUMIA le pedirá que las confirme.',
  slot_taken: 'Este horario acaba de ocuparse. Elija otro.',
  booking_failed: 'La reserva no se ha completado. Inténtelo de nuevo en un momento.',
  confirmed_title: 'Su entrevista está programada',
  confirmation_sent: (email: string) => `Se enviará una confirmación a ${email}.`,
  add_to_calendar: 'Añadir a mi calendario (.ics)',
  ics_summary: (company: string) => `Entrevista de preparación AYUMIA — ${company}`,
  ics_description: 'Entrevista de preparación de su entorno AYUMIA. El enlace de videoconferencia se enviará por correo.',
  ics_file: 'entrevista-ayumia.ics',
  understood_title: 'Lo que nos ha dicho',
  understood_lead: 'AYUMIA partirá de estas declaraciones y le pedirá que las confirme al inicio de la entrevista.',
  company: 'Empresa',
  declared: 'Declarado',
  probable_domains: (list: string) => `Temas probables de la entrevista: ${list}.`,
  objective_fact: 'Objetivo principal',
  prepare_title: 'Ayude a AYUMIA a preparar la entrevista',
  prepare_lead: 'Opcional. Cuanto más sepa AYUMIA de antemano, menos preguntas tendrá que hacerle.',
  prepare_skipped: 'Anotado: prepararemos la entrevista con lo que ya nos ha indicado.',
  prepare_file: 'Añadir un archivo',
  prepare_system: 'Conectar un sistema',
  prepare_note: 'Añadir información',
  prepare_skip: 'Omitir este paso',
  prepare_file_added: 'Archivo (simulado)',
  prepare_system_added: 'Conexión a un sistema (simulada)',
  note_label: 'Información para AYUMIA',
  add: 'Añadir',
  kind_file: 'Archivo',
  kind_system: 'Sistema',
  kind_note: 'Información',
  prepare_demo: 'Demostración: la carga de archivos y la conexión de sistemas son simuladas. Aquí nunca se le pedirán credenciales.',
  demo_reference: (id: string) => `Referencia de demostración: ${id}`,
  list_separator: ', ',
};

const it: Messages = {
  consultant_option: 'Desidero che un consulente AYUMIA partecipi al colloquio',
  consultant_help: 'Il colloquio è condotto dall’IA; un consulente può partecipare in aggiunta e intervenire se necessario.',
  start_interview: 'Avvia il colloquio (demo)',
  language: 'Lingua',
  demo_footnote: 'Dimostrazione: orari e prenotazione sono simulati, nessun dato viene inviato.',
  landing_title: 'Prepariamo il suo AYUMIA',
  landing_lead:
    'Prenoti un colloquio di un’ora. Ci dica chi è e cosa vuole coprire; AYUMIA prepara il colloquio in anticipo per porle solo le domande utili.',
  landing_step1_title: 'Scelga un orario',
  landing_step1_text: 'Il giorno e l’ora che preferisce.',
  landing_step2_title: 'Risponda ad alcune domande',
  landing_step2_text: 'La sua azienda, i suoi strumenti, cosa vuole gestire. Due minuti.',
  landing_step3_title: 'Il colloquio',
  landing_step3_text: 'AYUMIA le presenta ciò che ha capito, lei corregge, e prepara una proposta di configurazione da approvare.',
  landing_cta: 'Scegliere un orario',
  landing_note: 'Nulla viene configurato senza la sua approvazione.',
  event_label: 'Appuntamento',
  event_title: 'Colloquio di preparazione AYUMIA',
  event_channel: 'Videoconferenza (link inviato con la conferma)',
  event_description:
    'Un confronto guidato per capire la sua attività, gli strumenti attuali e cosa vuole migliorare. AYUMIA prepara il colloquio con le informazioni che ci fornisce qui; le sue proposte restano da approvare da parte sua.',
  minutes: (n: number) => `${n} min`,
  back_home: 'Torna all’inizio',
  schedule_title: 'Scelga data e ora',
  previous_month: 'Mese precedente',
  next_month: 'Mese successivo',
  days_of: (month: string) => `Giorni di ${month}`,
  day_slots: (day: string, n: number) => `${day}, ${n} ${n > 1 ? 'orari' : 'orario'}`,
  day_unavailable: (day: string) => `${day}, non disponibile`,
  slots_of: (day: string) => `Orari di ${day}`,
  availability_error: 'Impossibile caricare le disponibilità.',
  retry: 'Riprova',
  no_slot_month: 'Nessun orario libero questo mese. Provi il successivo.',
  no_slot_day: 'Nessun orario libero rimasto quel giorno.',
  time_zone: 'Fuso orario',
  next: 'Avanti',
  change_slot: 'Cambia orario',
  details_title: 'I suoi dati',
  section_company: 'La sua azienda',
  section_you: 'Lei',
  section_project: 'Il suo progetto',
  country: 'Paese',
  legal_name: 'Ragione sociale',
  website: 'Sito web',
  optional: 'Facoltativo.',
  contact_name: 'Nome e cognome',
  contact_role: 'Ruolo',
  email: 'E-mail',
  email_help: 'Per la conferma e il link del colloquio.',
  other_system: 'Quale altro strumento?',
  objective: 'Cosa vorrebbe migliorare per primo?',
  objective_placeholder: 'Per esempio: tracciare i lotti dal ricevimento alla spedizione, eliminare le doppie registrazioni tra Excel e fatturazione…',
  characters_left: (n: number) => `${n} ${n > 1 ? 'caratteri rimanenti' : 'carattere rimanente'}.`,
  required_mark: 'obbligatorio',
  valid: 'Valido',
  error_legal_name: 'Indichi il nome della sua azienda.',
  error_registration_format: 'Questo numero non ha il formato previsto.',
  error_registration_checksum: 'Questo numero non supera il controllo della cifra. Lo verifichi.',
  error_siren_format: 'Un SIREN ha 9 cifre, un SIRET 14.',
  error_website: 'Questo indirizzo web sembra incompleto.',
  error_contact_name: 'Indichi il suo nome.',
  error_email_missing: 'Indichi un indirizzo e-mail.',
  error_email_format: 'Questo indirizzo e-mail sembra incompleto.',
  error_domains: 'Scelga almeno un ambito.',
  error_form: (n: number) => `${n} ${n > 1 ? 'campi' : 'campo'} da correggere.`,
  submit: 'Pianifica il colloquio',
  submitting: 'Prenotazione…',
  privacy_note: 'Le sue risposte servono solo a preparare il colloquio. Restano dichiarazioni: AYUMIA le chiederà di confermarle.',
  slot_taken: 'Questo orario è appena stato prenotato. Ne scelga un altro.',
  booking_failed: 'La prenotazione non è andata a buon fine. Riprovi tra un momento.',
  confirmed_title: 'Il suo colloquio è pianificato',
  confirmation_sent: (email: string) => `Una conferma sarà inviata a ${email}.`,
  add_to_calendar: 'Aggiungi al mio calendario (.ics)',
  ics_summary: (company: string) => `Colloquio di preparazione AYUMIA — ${company}`,
  ics_description: 'Colloquio di preparazione del suo ambiente AYUMIA. Il link della videoconferenza sarà inviato via e-mail.',
  ics_file: 'colloquio-ayumia.ics',
  understood_title: 'Ciò che ci ha detto',
  understood_lead: 'AYUMIA partirà da queste dichiarazioni e le chiederà di confermarle all’inizio del colloquio.',
  company: 'Azienda',
  declared: 'Dichiarato',
  probable_domains: (list: string) => `Temi probabili del colloquio: ${list}.`,
  objective_fact: 'Obiettivo principale',
  prepare_title: 'Aiuti AYUMIA a preparare il colloquio',
  prepare_lead: 'Facoltativo. Più AYUMIA sa in anticipo, meno domande dovrà farle.',
  prepare_skipped: 'Preso nota: prepareremo il colloquio con ciò che ha già indicato.',
  prepare_file: 'Aggiungi un file',
  prepare_system: 'Collega un sistema',
  prepare_note: 'Aggiungi un’informazione',
  prepare_skip: 'Salta questo passaggio',
  prepare_file_added: 'File (simulato)',
  prepare_system_added: 'Collegamento a un sistema (simulato)',
  note_label: 'Informazione per AYUMIA',
  add: 'Aggiungi',
  kind_file: 'File',
  kind_system: 'Sistema',
  kind_note: 'Informazione',
  prepare_demo: 'Dimostrazione: l’aggiunta di file e il collegamento di sistemi sono simulati. Qui non le verranno mai chieste credenziali.',
  demo_reference: (id: string) => `Riferimento di dimostrazione: ${id}`,
  list_separator: ', ',
};

const pt: Messages = {
  consultant_option: 'Gostaria que um consultor AYUMIA assistisse à entrevista',
  consultant_help: 'A entrevista é conduzida pela IA; um consultor pode também assistir e intervir se necessário.',
  start_interview: 'Iniciar a entrevista (demo)',
  language: 'Idioma',
  demo_footnote: 'Demonstração: os horários e a reserva são simulados; nenhum dado é enviado.',
  landing_title: 'Vamos preparar o seu AYUMIA',
  landing_lead:
    'Reserve uma entrevista de uma hora. Diga-nos quem é e o que quer abranger; o AYUMIA prepara a entrevista com antecedência para lhe fazer apenas as perguntas úteis.',
  landing_step1_title: 'Escolha um horário',
  landing_step1_text: 'O dia e a hora que lhe convêm.',
  landing_step2_title: 'Responda a algumas perguntas',
  landing_step2_text: 'A sua empresa, as suas ferramentas, o que quer gerir. Dois minutos.',
  landing_step3_title: 'A entrevista',
  landing_step3_text: 'O AYUMIA apresenta o que compreendeu, o senhor corrige, e ele prepara uma proposta de configuração para validar.',
  landing_cta: 'Escolher um horário',
  landing_note: 'Nada é configurado sem a sua validação.',
  event_label: 'Marcação',
  event_title: 'Entrevista de preparação AYUMIA',
  event_channel: 'Videoconferência (link enviado com a confirmação)',
  event_description:
    'Uma conversa guiada para compreender a sua atividade, as suas ferramentas atuais e o que quer melhorar. O AYUMIA prepara a entrevista com as informações que nos dá aqui; as suas propostas ficam sujeitas à sua validação.',
  minutes: (n: number) => `${n} min`,
  back_home: 'Voltar ao início',
  schedule_title: 'Escolha uma data e uma hora',
  previous_month: 'Mês anterior',
  next_month: 'Mês seguinte',
  days_of: (month: string) => `Dias de ${month}`,
  day_slots: (day: string, n: number) => `${day}, ${n} ${n > 1 ? 'horários' : 'horário'}`,
  day_unavailable: (day: string) => `${day}, indisponível`,
  slots_of: (day: string) => `Horários de ${day}`,
  availability_error: 'Não foi possível carregar as disponibilidades.',
  retry: 'Tentar de novo',
  no_slot_month: 'Nenhum horário livre este mês. Experimente o seguinte.',
  no_slot_day: 'Já não há horários livres nesse dia.',
  time_zone: 'Fuso horário',
  next: 'Seguinte',
  change_slot: 'Mudar de horário',
  details_title: 'Os seus dados',
  section_company: 'A sua empresa',
  section_you: 'O senhor / a senhora',
  section_project: 'O seu projeto',
  country: 'País',
  legal_name: 'Denominação social',
  website: 'Sítio web',
  optional: 'Opcional.',
  contact_name: 'Nome completo',
  contact_role: 'Função',
  email: 'E-mail',
  email_help: 'Para a confirmação e o link da entrevista.',
  other_system: 'Que outra ferramenta?',
  objective: 'O que gostaria de melhorar em primeiro lugar?',
  objective_placeholder: 'Por exemplo: seguir os lotes da receção à expedição, acabar com a dupla introdução entre Excel e faturação…',
  characters_left: (n: number) => `${n} ${n > 1 ? 'caracteres restantes' : 'carácter restante'}.`,
  required_mark: 'obrigatório',
  valid: 'Válido',
  error_legal_name: 'Indique o nome da sua empresa.',
  error_registration_format: 'Este número não tem o formato esperado.',
  error_registration_checksum: 'Este número não passa no controlo do dígito. Verifique-o.',
  error_siren_format: 'Um SIREN tem 9 algarismos, um SIRET 14.',
  error_website: 'Este endereço web parece incompleto.',
  error_contact_name: 'Indique o seu nome.',
  error_email_missing: 'Indique um endereço de e-mail.',
  error_email_format: 'Este endereço de e-mail parece incompleto.',
  error_domains: 'Escolha pelo menos uma área.',
  error_form: (n: number) => `${n} ${n > 1 ? 'campos' : 'campo'} a corrigir.`,
  submit: 'Agendar a entrevista',
  submitting: 'A reservar…',
  privacy_note: 'As suas respostas servem apenas para preparar a entrevista. Continuam a ser declarações: o AYUMIA pedir-lhe-á que as confirme.',
  slot_taken: 'Este horário acabou de ser reservado. Escolha outro.',
  booking_failed: 'A reserva não foi concluída. Tente de novo dentro de momentos.',
  confirmed_title: 'A sua entrevista está agendada',
  confirmation_sent: (email: string) => `Será enviada uma confirmação para ${email}.`,
  add_to_calendar: 'Adicionar ao meu calendário (.ics)',
  ics_summary: (company: string) => `Entrevista de preparação AYUMIA — ${company}`,
  ics_description: 'Entrevista de preparação do seu ambiente AYUMIA. O link da videoconferência será enviado por e-mail.',
  ics_file: 'entrevista-ayumia.ics',
  understood_title: 'O que nos disse',
  understood_lead: 'O AYUMIA partirá destas declarações e pedir-lhe-á que as confirme no início da entrevista.',
  company: 'Empresa',
  declared: 'Declarado',
  probable_domains: (list: string) => `Temas prováveis da entrevista: ${list}.`,
  objective_fact: 'Objetivo principal',
  prepare_title: 'Ajude o AYUMIA a preparar a entrevista',
  prepare_lead: 'Opcional. Quanto mais o AYUMIA souber antes, menos perguntas terá de fazer.',
  prepare_skipped: 'Registado: prepararemos a entrevista com o que já indicou.',
  prepare_file: 'Adicionar um ficheiro',
  prepare_system: 'Ligar um sistema',
  prepare_note: 'Adicionar uma informação',
  prepare_skip: 'Saltar este passo',
  prepare_file_added: 'Ficheiro (simulado)',
  prepare_system_added: 'Ligação a um sistema (simulada)',
  note_label: 'Informação para o AYUMIA',
  add: 'Adicionar',
  kind_file: 'Ficheiro',
  kind_system: 'Sistema',
  kind_note: 'Informação',
  prepare_demo: 'Demonstração: a adição de ficheiros e a ligação de sistemas são simuladas. Nunca lhe serão pedidas credenciais aqui.',
  demo_reference: (id: string) => `Referência de demonstração: ${id}`,
  list_separator: ', ',
};

const nl: Messages = {
  consultant_option: 'Ik wil graag dat een AYUMIA-consultant het gesprek bijwoont',
  consultant_help: 'Het gesprek wordt door de AI gevoerd; een consultant kan ook aanwezig zijn en zo nodig ingrijpen.',
  start_interview: 'Gesprek starten (demo)',
  language: 'Taal',
  demo_footnote: 'Demo: tijdsloten en boeking zijn gesimuleerd, er worden geen gegevens verzonden.',
  landing_title: 'Laten we uw AYUMIA voorbereiden',
  landing_lead:
    'Plan een gesprek van een uur. Vertel ons wie u bent en wat u wilt afdekken; AYUMIA bereidt het gesprek vooraf voor en stelt alleen de nuttige vragen.',
  landing_step1_title: 'Kies een tijdstip',
  landing_step1_text: 'De dag en het uur die u uitkomen.',
  landing_step2_title: 'Beantwoord een paar vragen',
  landing_step2_text: 'Uw bedrijf, uw tools, wat u wilt beheren. Twee minuten.',
  landing_step3_title: 'Het gesprek',
  landing_step3_text: 'AYUMIA toont wat het begrepen heeft, u corrigeert, en het maakt een configuratievoorstel ter goedkeuring.',
  landing_cta: 'Kies een tijdstip',
  landing_note: 'Niets wordt geconfigureerd zonder uw goedkeuring.',
  event_label: 'Afspraak',
  event_title: 'AYUMIA-voorbereidingsgesprek',
  event_channel: 'Videogesprek (link bij de bevestiging)',
  event_description:
    'Een begeleid gesprek om uw activiteiten, uw huidige tools en uw verbeterwensen te begrijpen. AYUMIA bereidt het gesprek voor met wat u hier deelt; de voorstellen keurt u zelf goed.',
  minutes: (n: number) => `${n} min`,
  back_home: 'Terug naar het begin',
  schedule_title: 'Kies een datum en tijd',
  previous_month: 'Vorige maand',
  next_month: 'Volgende maand',
  days_of: (month: string) => `Dagen van ${month}`,
  day_slots: (day: string, n: number) => `${day}, ${n} ${n > 1 ? 'tijdsloten' : 'tijdslot'}`,
  day_unavailable: (day: string) => `${day}, niet beschikbaar`,
  slots_of: (day: string) => `Tijdsloten op ${day}`,
  availability_error: 'De beschikbaarheid kon niet worden geladen.',
  retry: 'Opnieuw proberen',
  no_slot_month: 'Geen vrij tijdslot deze maand. Probeer de volgende.',
  no_slot_day: 'Geen vrij tijdslot meer op die dag.',
  time_zone: 'Tijdzone',
  next: 'Volgende',
  change_slot: 'Ander tijdslot kiezen',
  details_title: 'Uw gegevens',
  section_company: 'Uw bedrijf',
  section_you: 'U',
  section_project: 'Uw project',
  country: 'Land',
  legal_name: 'Bedrijfsnaam',
  website: 'Website',
  optional: 'Optioneel.',
  contact_name: 'Voor- en achternaam',
  contact_role: 'Functie',
  email: 'E-mail',
  email_help: 'Voor de bevestiging en de link naar het gesprek.',
  other_system: 'Welke andere tool?',
  objective: 'Wat wilt u als eerste verbeteren?',
  objective_placeholder: 'Bijvoorbeeld: partijen volgen van ontvangst tot verzending, dubbele invoer tussen Excel en facturatie stoppen…',
  characters_left: (n: number) => `Nog ${n} ${n > 1 ? 'tekens' : 'teken'}.`,
  required_mark: 'verplicht',
  valid: 'Geldig',
  error_legal_name: 'Vul de naam van uw bedrijf in.',
  error_registration_format: 'Dit nummer heeft niet het verwachte formaat.',
  error_registration_checksum: 'Dit nummer doorstaat de controle van het controlegetal niet. Controleer het.',
  error_siren_format: 'Een SIREN heeft 9 cijfers, een SIRET 14.',
  error_website: 'Dit webadres lijkt onvolledig.',
  error_contact_name: 'Vul uw naam in.',
  error_email_missing: 'Vul een e-mailadres in.',
  error_email_format: 'Dit e-mailadres lijkt onvolledig.',
  error_domains: 'Kies minstens één domein.',
  error_form: (n: number) => `${n} ${n > 1 ? 'velden' : 'veld'} te corrigeren.`,
  submit: 'Gesprek inplannen',
  submitting: 'Bezig met boeken…',
  privacy_note: 'Uw antwoorden dienen alleen om het gesprek voor te bereiden. Het blijven verklaringen: AYUMIA vraagt u ze te bevestigen.',
  slot_taken: 'Dit tijdslot is net geboekt. Kies een ander.',
  booking_failed: 'De boeking is niet gelukt. Probeer het zo meteen opnieuw.',
  confirmed_title: 'Uw gesprek is ingepland',
  confirmation_sent: (email: string) => `Er wordt een bevestiging gestuurd naar ${email}.`,
  add_to_calendar: 'Toevoegen aan mijn agenda (.ics)',
  ics_summary: (company: string) => `AYUMIA-voorbereidingsgesprek — ${company}`,
  ics_description: 'Voorbereidingsgesprek voor uw AYUMIA-omgeving. De link naar het videogesprek wordt per e-mail verstuurd.',
  ics_file: 'ayumia-gesprek.ics',
  understood_title: 'Wat u ons vertelde',
  understood_lead: 'AYUMIA vertrekt van deze verklaringen en vraagt u ze aan het begin van het gesprek te bevestigen.',
  company: 'Bedrijf',
  declared: 'Verklaard',
  probable_domains: (list: string) => `Waarschijnlijke onderwerpen van het gesprek: ${list}.`,
  objective_fact: 'Hoofddoel',
  prepare_title: 'Help AYUMIA het gesprek voor te bereiden',
  prepare_lead: 'Optioneel. Hoe meer AYUMIA vooraf weet, hoe minder vragen het hoeft te stellen.',
  prepare_skipped: 'Genoteerd: we bereiden het gesprek voor met wat u al hebt gedeeld.',
  prepare_file: 'Bestand toevoegen',
  prepare_system: 'Systeem koppelen',
  prepare_note: 'Informatie toevoegen',
  prepare_skip: 'Deze stap overslaan',
  prepare_file_added: 'Bestand (gesimuleerd)',
  prepare_system_added: 'Systeemkoppeling (gesimuleerd)',
  note_label: 'Informatie voor AYUMIA',
  add: 'Toevoegen',
  kind_file: 'Bestand',
  kind_system: 'Systeem',
  kind_note: 'Informatie',
  prepare_demo: 'Demo: bestanden toevoegen en systemen koppelen zijn gesimuleerd. Hier wordt nooit om inloggegevens gevraagd.',
  demo_reference: (id: string) => `Demoreferentie: ${id}`,
  list_separator: ', ',
};

const ar: Messages = {
  consultant_option: 'أرغب في حضور مستشار من AYUMIA للمقابلة',
  consultant_help: 'يدير الذكاءُ الاصطناعي المقابلة، ويمكن لمستشار أن يحضر أيضًا ويتدخل عند الحاجة.',
  start_interview: 'بدء المقابلة (عرض توضيحي)',
  language: 'اللغة',
  demo_footnote: 'عرض توضيحي: المواعيد والحجز محاكاة، ولا تُرسَل أي بيانات.',
  landing_title: 'لنُعِدّ AYUMIA الخاص بكم',
  landing_lead:
    'احجزوا مقابلة مدتها ساعة. أخبرونا من أنتم وما الذي تريدون تغطيته؛ يُعِدّ AYUMIA المقابلة مسبقًا ليطرح عليكم الأسئلة المفيدة فقط.',
  landing_step1_title: 'اختاروا موعدًا',
  landing_step1_text: 'اليوم والساعة المناسبان لكم.',
  landing_step2_title: 'أجيبوا عن بعض الأسئلة',
  landing_step2_text: 'شركتكم، وأدواتكم، وما تريدون إدارته. دقيقتان.',
  landing_step3_title: 'المقابلة',
  landing_step3_text: 'يعرض عليكم AYUMIA ما فهمه، فتصحّحون، ثم يُعِدّ مقترح إعداد لتصادقوا عليه.',
  landing_cta: 'اختيار موعد',
  landing_note: 'لا يُعَدّ أي شيء دون مصادقتكم.',
  event_label: 'الموعد',
  event_title: 'مقابلة التحضير لـ AYUMIA',
  event_channel: 'مكالمة فيديو (يُرسَل الرابط مع التأكيد)',
  event_description:
    'حوار موجَّه لفهم نشاطكم وأدواتكم الحالية وما تريدون تحسينه. يُعِدّ AYUMIA المقابلة انطلاقًا من المعلومات التي تقدّمونها هنا، وتبقى مقترحاته رهن مصادقتكم.',
  minutes: (n: number) => `${n} دقيقة`,
  back_home: 'العودة إلى البداية',
  schedule_title: 'اختاروا تاريخًا وساعة',
  previous_month: 'الشهر السابق',
  next_month: 'الشهر التالي',
  days_of: (month: string) => `أيام ${month}`,
  day_slots: (day: string, n: number) => `${day}، ${n} ${n === 1 ? 'موعد متاح' : 'مواعيد متاحة'}`,
  day_unavailable: (day: string) => `${day}، غير متاح`,
  slots_of: (day: string) => `مواعيد يوم ${day}`,
  availability_error: 'تعذّر تحميل المواعيد المتاحة.',
  retry: 'إعادة المحاولة',
  no_slot_month: 'لا يوجد موعد متاح هذا الشهر. جرّبوا الشهر التالي.',
  no_slot_day: 'لم يعد هناك موعد متاح في ذلك اليوم.',
  time_zone: 'المنطقة الزمنية',
  next: 'التالي',
  change_slot: 'تغيير الموعد',
  details_title: 'معلوماتكم',
  section_company: 'شركتكم',
  section_you: 'أنتم',
  section_project: 'مشروعكم',
  country: 'البلد',
  legal_name: 'الاسم التجاري للشركة',
  website: 'الموقع الإلكتروني',
  optional: 'اختياري.',
  contact_name: 'الاسم الكامل',
  contact_role: 'المنصب',
  email: 'البريد الإلكتروني',
  email_help: 'لإرسال التأكيد ورابط المقابلة.',
  other_system: 'ما الأداة الأخرى؟',
  objective: 'ما الذي تودّون تحسينه أولًا؟',
  objective_placeholder: 'مثلًا: تتبّع الدفعات من الاستلام حتى الشحن، أو إنهاء الإدخال المزدوج بين Excel والفوترة…',
  characters_left: (n: number) => `${n} حرفًا متبقيًا.`,
  required_mark: 'إلزامي',
  valid: 'صالح',
  error_legal_name: 'أدخلوا اسم شركتكم.',
  error_registration_format: 'هذا الرقم ليس بالصيغة المتوقعة.',
  error_registration_checksum: 'هذا الرقم لا يجتاز التحقق من رقم المراجعة. يرجى التحقق منه.',
  error_siren_format: 'يتكوّن رقم SIREN من 9 أرقام، ورقم SIRET من 14 رقمًا.',
  error_website: 'يبدو أن عنوان الموقع غير مكتمل.',
  error_contact_name: 'أدخلوا اسمكم.',
  error_email_missing: 'أدخلوا عنوان بريد إلكتروني.',
  error_email_format: 'يبدو أن عنوان البريد الإلكتروني غير مكتمل.',
  error_domains: 'اختاروا مجالًا واحدًا على الأقل.',
  error_form: (n: number) => (n === 1 ? 'حقل واحد يحتاج إلى تصحيح.' : `${n} حقول تحتاج إلى تصحيح.`),
  submit: 'تحديد موعد المقابلة',
  submitting: 'جارٍ الحجز…',
  privacy_note: 'تُستخدم إجاباتكم فقط لتحضير المقابلة، وتبقى تصريحات: سيطلب منكم AYUMIA تأكيدها.',
  slot_taken: 'حُجز هذا الموعد للتوّ. يرجى اختيار موعد آخر.',
  booking_failed: 'لم يكتمل الحجز. يرجى المحاولة مجددًا بعد قليل.',
  confirmed_title: 'تم تحديد موعد مقابلتكم',
  confirmation_sent: (email: string) => `سيُرسَل تأكيد إلى ${email}.`,
  add_to_calendar: 'إضافة إلى تقويمي (‎.ics)',
  ics_summary: (company: string) => `مقابلة التحضير لـ AYUMIA — ${company}`,
  ics_description: 'مقابلة تحضير بيئة AYUMIA الخاصة بكم. سيُرسَل رابط مكالمة الفيديو عبر البريد الإلكتروني.',
  ics_file: 'ayumia-interview.ics',
  understood_title: 'ما أخبرتمونا به',
  understood_lead: 'سينطلق AYUMIA من هذه التصريحات وسيطلب منكم تأكيدها في بداية المقابلة.',
  company: 'الشركة',
  declared: 'مُصرَّح به',
  probable_domains: (list: string) => `المواضيع المرجّحة للمقابلة: ${list}.`,
  objective_fact: 'الهدف الرئيسي',
  prepare_title: 'ساعدوا AYUMIA على تحضير المقابلة',
  prepare_lead: 'اختياري. كلما عرف AYUMIA أكثر مسبقًا، قلّت الأسئلة التي سيطرحها عليكم.',
  prepare_skipped: 'تم التسجيل: سنحضّر المقابلة بما قدّمتموه حتى الآن.',
  prepare_file: 'إضافة ملف',
  prepare_system: 'ربط نظام',
  prepare_note: 'إضافة معلومة',
  prepare_skip: 'تخطّي هذه الخطوة',
  prepare_file_added: 'ملف (محاكاة)',
  prepare_system_added: 'ربط بنظام (محاكاة)',
  note_label: 'معلومة لـ AYUMIA',
  add: 'إضافة',
  kind_file: 'ملف',
  kind_system: 'نظام',
  kind_note: 'معلومة',
  prepare_demo: 'عرض توضيحي: إضافة الملفات وربط الأنظمة محاكاة. لن يُطلب منكم هنا أي بيانات دخول.',
  demo_reference: (id: string) => `مرجع العرض التوضيحي: ${id}`,
  list_separator: '، ',
};

export const MESSAGES: Record<Locale, Messages> = { fr, en, de, es, it, pt, nl, ar };

/** Sens d'écriture de la langue : l'arabe s'écrit de droite à gauche. */
export function directionOf(locale: Locale): 'ltr' | 'rtl' {
  return locale === 'ar' ? 'rtl' : 'ltr';
}
